import React, { useEffect, useMemo } from 'react';
import { Building2, Loader2, MapPin, X } from 'lucide-react';
import { marked } from 'marked';

function looksLikeHtml(s: string): boolean {
  return /<[a-z][\s\S]*>/i.test(s);
}

/** Ruwe recruiter-HTML: verwijder gevaarlijke tags/attributen vóór weergave in het portaal. */
function sanitizeVacancyHtml(html: string): string {
  let s = String(html || '');
  const blocks = ['script', 'style', 'iframe', 'object', 'embed', 'form', 'meta', 'link'];
  for (const tag of blocks) {
    const re = new RegExp(`<${tag}\\b[^>]*>[\\s\\S]*?<\\/${tag}>`, 'gi');
    s = s.replace(re, '');
  }
  s = s.replace(/<(?:script|style|iframe|object|embed|form)\b[^>]*\/?>/gi, '');
  s = s.replace(/on\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '');
  s = s.replace(/javascript:/gi, '');
  return s;
}

function decodeEntities(text: string): string {
  if (typeof document === 'undefined') return text;
  const t = document.createElement('textarea');
  t.innerHTML = text;
  return t.value;
}

const proseVacancyClasses =
  'prose prose-sm prose-slate max-w-none text-slate-700 ' +
  '[&_p]:my-3 [&_p]:leading-relaxed [&_br]:block ' +
  '[&_h1]:text-xl [&_h1]:font-bold [&_h1]:text-slate-900 [&_h1]:mt-8 [&_h1]:mb-3 [&_h1]:first:mt-0 ' +
  '[&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:mt-6 [&_h2]:mb-2 ' +
  '[&_h3]:text-base [&_h3]:font-bold [&_h3]:text-slate-900 [&_h3]:mt-5 [&_h3]:mb-2 ' +
  '[&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-5 ' +
  '[&_li]:my-1 [&_li]:leading-relaxed [&_strong]:font-semibold [&_b]:font-semibold ' +
  '[&_a]:text-indigo-600 [&_a]:underline [&_a]:decoration-indigo-200 [&_blockquote]:border-l-4 [&_blockquote]:border-slate-200 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-slate-600';

export interface EngagementVacancyModalProps {
  open: boolean;
  loading: boolean;
  error: string | null;
  vacancy: Record<string, any> | null;
  onClose: () => void;
}

const EngagementVacancyModal: React.FC<EngagementVacancyModalProps> = ({ open, loading, error, vacancy, onClose }) => {
  const bodyHtml = useMemo(() => {
    if (!vacancy) return '';
    const finalContent = vacancy?.data?.finalVacancyContent;
    if (typeof finalContent === 'string' && finalContent.trim()) {
      return sanitizeVacancyHtml(finalContent.trim());
    }
    const desc = vacancy?.description != null ? String(vacancy.description) : '';
    if (desc.trim()) {
      if (looksLikeHtml(desc)) {
        return sanitizeVacancyHtml(desc.trim());
      }
      try {
        const parsed = marked.parse(desc.trim(), { async: false }) as string;
        return sanitizeVacancyHtml(parsed);
      } catch {
        return sanitizeVacancyHtml(`<p>${decodeEntities(desc.trim()).replace(/</g, '&lt;')}</p>`);
      }
    }
    return '';
  }, [vacancy]);

  const responsibilitiesHtml = useMemo(() => {
    if (!vacancy?.responsibilities) return '';
    const r = vacancy.responsibilities;
    if (typeof r === 'string' && r.trim()) {
      if (looksLikeHtml(r)) return sanitizeVacancyHtml(r.trim());
      try {
        return sanitizeVacancyHtml(marked.parse(r.trim(), { async: false }) as string);
      } catch {
        return '';
      }
    }
    return '';
  }, [vacancy]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const ek = Array.isArray(vacancy?.eindklant) ? vacancy.eindklant[0] : vacancy?.eindklant;
  const og = Array.isArray(vacancy?.opdrachtgever) ? vacancy.opdrachtgever[0] : vacancy?.opdrachtgever;
  const loc =
    vacancy?.data?.location ||
    vacancy?.data?.city ||
    vacancy?.data?.werkplek ||
    (typeof vacancy?.data?.address === 'object' && vacancy?.data?.address?.city) ||
    '';

  return (
    <div className="fixed inset-0 z-[200] flex justify-end" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/45 backdrop-blur-[2px] transition-opacity"
        aria-label="Sluit venster"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="eng-vac-title"
        className="relative flex h-full w-full max-w-md flex-col bg-white shadow-[-12px_0_40px_rgba(15,23,42,0.12)] sm:max-w-lg md:max-w-xl lg:max-w-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 bg-white px-5 py-4 md:px-6">
          <div className="min-w-0 pr-2">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Vacature</p>
            <h2 id="eng-vac-title" className="mt-1 text-lg font-semibold leading-snug text-slate-900 md:text-xl">
              {loading ? 'Laden…' : vacancy?.title || 'Vacature'}
            </h2>
            {!loading && vacancy?.status ? (
              <p className="mt-1 text-xs font-medium text-slate-500">Status: {String(vacancy.status)}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label="Sluiten"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto custom-scrollbar">
          <div className="space-y-5 px-5 py-5 md:px-6 md:py-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center gap-3 py-20 text-slate-500">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                <p className="text-sm font-medium">Vacaturegegevens laden…</p>
              </div>
            ) : error ? (
              <p className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>
            ) : vacancy ? (
              <>
                {(ek?.name || og?.name) ? (
                  <div className="flex flex-wrap gap-4 text-sm">
                    {ek?.name ? (
                      <div className="flex items-center gap-2 text-slate-700">
                        <Building2 className="h-4 w-4 shrink-0 text-slate-400" />
                        <span>
                          <span className="font-semibold text-slate-900">Werkplek: </span>
                          {ek.name}
                        </span>
                      </div>
                    ) : null}
                    {og?.name && og?.id !== ek?.id ? (
                      <div className="flex items-center gap-2 text-slate-700">
                        <Building2 className="h-4 w-4 shrink-0 text-slate-400" />
                        <span>
                          <span className="font-semibold text-slate-900">Opdrachtgever: </span>
                          {og.name}
                        </span>
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {loc ? (
                  <div className="flex items-start gap-2 text-sm text-slate-700">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                    <span>{String(loc)}</span>
                  </div>
                ) : null}
                {vacancy?.data?.category ? (
                  <p className="text-sm text-slate-600">
                    <span className="font-semibold text-slate-800">Categorie: </span>
                    {String(vacancy.data.category)}
                  </p>
                ) : null}

                {bodyHtml ? (
                  <section className="border-t border-slate-100 pt-5">
                    <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Omschrijving</h3>
                    <div
                      className={`mt-3 ${proseVacancyClasses}`}
                      dangerouslySetInnerHTML={{ __html: bodyHtml }}
                    />
                  </section>
                ) : (
                  <p className="text-sm text-slate-500">Geen volledige vacaturetekst beschikbaar in dit overzicht.</p>
                )}

                {responsibilitiesHtml ? (
                  <section className="border-t border-slate-100 pt-5">
                    <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Wat je gaat doen</h3>
                    <div
                      className={`mt-3 ${proseVacancyClasses}`}
                      dangerouslySetInnerHTML={{ __html: responsibilitiesHtml }}
                    />
                  </section>
                ) : null}

                {!responsibilitiesHtml &&
                typeof vacancy.responsibilities === 'object' &&
                vacancy.responsibilities &&
                !Array.isArray(vacancy.responsibilities) ? (
                  <section className="border-t border-slate-100 pt-5">
                    <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Wat je gaat doen</h3>
                    <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-relaxed text-slate-700">
                      {Object.entries(vacancy.responsibilities as Record<string, unknown>).map(([key, val]) => (
                        <li key={key}>{String(val)}</li>
                      ))}
                    </ul>
                  </section>
                ) : null}
              </>
            ) : (
              <p className="text-sm text-slate-500">Geen gegevens.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EngagementVacancyModal;
